import { forwardRef } from 'react'

import useGetSetting from 'js/page/Widget/hooks/useGetSetting'
import { WidgetLink } from 'js/page/Widget/types/WidgetType'

import LinkListEdit from './LinkListEdit/LinkListEdit'
import LinkListView from './LinkListView/LinkListView'

export interface WidgetSetting {
    key: string
    value?: string
    links?: WidgetLink[]
}

export interface Entity {
    settings?: WidgetSetting[]
    userSettings?: { key: string; value: string }[]
    [key: string]: any
}

type LayoutType = 'carousel' | 'grid' | 'list'
export type TemplateType = string[]
type ItemSizeType = 'sm' | 'md' | 'lg'

export interface LinkListSettings {
    title: string
    layout: LayoutType
    template: TemplateType
    links: WidgetLink[]
    itemSize: ItemSizeType
    configurable: boolean
    enableNumberOfItems: boolean
    numberOfItems: number
}

export interface LinkListProps {
    guid: string
    containerGuid?: string
    entity: Entity
    isEditing?: boolean
    editMode?: boolean
    onSave?: (
        settings: (
            | {
                  [x: string]: any
                  key: string
                  value?: undefined
              }
            | {
                  key: string
                  value: any
              }
        )[],
    ) => void
}

const LinkList = forwardRef(
    (
        {
            guid,
            containerGuid,
            entity,
            isEditing,
            editMode,
            onSave,
        }: LinkListProps,
        ref,
    ) => {
        const getSetting = useGetSetting(entity)

        const getLinks = (key: string, defaultValue: WidgetLink[] = []) => {
            const setting = entity?.settings?.find(
                (setting: WidgetSetting) => setting.key === key,
            )
            return setting ? setting.links || defaultValue : defaultValue
        }

        const settings: LinkListSettings = {
            title: getSetting('title', ''),
            layout: getSetting('layout', 'list') as LayoutType,
            template: getSetting('template')?.split(',') || [],
            links: getLinks('links', []),
            itemSize: getSetting('itemSize', 'md') as ItemSizeType, // Used in layout 'carousel'
            configurable: getSetting('configurable') === 'true',
            enableNumberOfItems:
                getSetting('enableNumberOfItems', 'false') === 'true',
            numberOfItems: parseInt(getSetting('numberOfItems', '5'), 10),
        }

        if (isEditing) {
            return (
                <LinkListEdit
                    ref={ref}
                    guid={guid}
                    settings={settings}
                    onSave={onSave}
                    containerGuid={containerGuid}
                />
            )
        }

        return (
            <LinkListView
                guid={guid}
                containerGuid={containerGuid}
                settings={settings}
                userSettings={entity.userSettings}
                editMode={editMode}
            />
        )
    },
)

LinkList.displayName = 'LinkList'

export default LinkList
